/**
 * The tenant hub body — the app's home dashboard content.
 *
 * This is the routed body only: the surrounding chrome (header, BottomNav) is
 * TenantShell's, so the hub gets the same frame as every other page. It greets
 * the user, shows one "Where to?" diagram of the SystemDox
 * flow with the Docs library at its centre, and renders the workspace dashboard
 * below. The dashboard's workspace-dependent content lives in
 * ./launch/LaunchDashboard (which mounts its own workspace context), keeping
 * this to hub duties.
 *
 * The "Where to?" diagram is two compact rows around Docs — the doc library you
 * organise and edit, named for the nav pillar it links to — the hub of the
 * whole solution. The top row is the pipeline: Capture feeds Docs, which
 * publishes to the Knowledge Base your wider org reads — capture it, document
 * it, publish it. The bottom row is the tools working off that library: Plan —
 * the standalone planning tool, a deliberate tool pillar rather than a
 * documentation pillar (so it lives here, never on the /docs hub) — hands the
 * board it populates off to Connect, the AI agents you build with, which sit
 * directly under Docs on the two-way SystemDox MCP link; Insights (gap
 * analysis) sits at the end of that row on the two-way Refine loop back into
 * Docs. Docs therefore carries four edges out of a grid that gives any box
 * three neighbours, so the one that goes diagonal — Refine — is drawn as an
 * elbow from Docs' bottom edge out to Insights rather than laid out as a
 * straight line. Every box is one uniform, unnumbered card of the same size (the arrows
 * carry the order); the connectors share one style and centre on the card
 * mid-line; the central Docs box is tinted so it reads as the hub. Each box
 * links to where that work happens (Capture app, /docs, /insights, docs portal,
 * /plan, /connect). The Connect box is named for the nav pillar it opens — an
 * exact box↔nav↔URL match like the others — while its copy keeps the actors
 * (Claude, Codex, Cursor) as the counterparty on the MCP edge.
 * Capture's label and copy still come from `@systemdox/surface-header`
 * — the module that drives the switcher — so that node cannot drift from it;
 * the other boxes are named for the nav pillars and surfaces they open.
 * Icons are the SystemDox family (components/icons/SdxIcon), not stock lucide.
 *
 * It owns its own scroll column (flex-1 overflow-y-auto) because TenantShell's
 * <main> is bounded and non-scrolling. The neutral host renders this same body
 * inside LaunchPage's minimal shell instead of TenantShell.
 */

import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import {
  IconAgents,
  IconCapture,
  IconDocs,
  IconGap,
  IconKnowledgeBase,
  IconPlan,
  type SdxIconProps,
} from '@/components/icons/SdxIcon';
import { SURFACES, type Surface, type SurfaceKey } from '@systemdox/surface-header';
import { PROJECT_PATH_PREFIX } from '@systemdox/types';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useProject } from '@/contexts/ProjectContext';
import LaunchDashboard from '@/pages/launch/LaunchDashboard';
import {
  captureAppUrl,
  getTenantSlug,
  rememberedTenantSlug,
  tenantUrl,
} from '@/utils/subdomain';
import { cn } from '@/lib/utils';

// Every box is the same card: same padding, and a min-height so all five end up
// the same size whatever their copy runs to.
const CARD_BASE =
  'group flex h-full min-h-[13rem] flex-col gap-3 rounded-xl border p-5 text-left transition-all';

interface FlowNode {
  label: string;
  Icon: (props: SdxIconProps) => ReactNode;
  /** Tailwind gradient for the icon chip; unused while disabled. */
  gradient: string;
  description: string;
  href?: string;
  disabled?: boolean;
}

/** One box in the flow. The arrows carry the pipeline's order — the cards are
 *  deliberately unnumbered so the two satellites don't read as missing steps.
 *  `highlight` tints the Docs hub. */
function FlowCard({ node, highlight = false }: { node: FlowNode; highlight?: boolean }) {
  const { label, Icon, gradient, description, href, disabled } = node;
  const shell = cn(
    CARD_BASE,
    highlight
      ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-950/40 ring-1 ring-inset ring-teal-500/30 shadow-sm'
      : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900',
    href && !disabled && 'hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md',
    disabled && 'opacity-60',
  );
  const body = (
    <>
      <div
        className={cn(
          'w-11 h-11 rounded-xl flex items-center justify-center',
          disabled ? 'bg-slate-200 dark:bg-slate-800' : cn('bg-gradient-to-br', gradient),
        )}
      >
        <Icon className={cn('w-5 h-5', disabled ? 'text-slate-400' : 'text-white')} />
      </div>
      <div>
        <div className="text-base font-semibold text-slate-900 dark:text-slate-100">{label}</div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{description}</p>
      </div>
    </>
  );

  if (disabled || !href) {
    return <div className={shell}>{body}</div>;
  }
  return (
    <a href={href} className={shell}>
      {body}
    </a>
  );
}

/**
 * Connectors. All four share one style — the same slate colour, the same label
 * typography, the same line and arrowheads — so the diagram reads as one family.
 * Directionality is the only thing that varies: the flow steps (Capture→Docs,
 * Published) get one head; the loops (Refine, MCP) get a head at each end.
 */

/** Horizontal on `sm`+, vertical once the diagram stacks. A fixed-height label
 *  cap above the arrow (mirrored by a spacer below) keeps the arrow on the card
 *  mid-line, so both horizontal connectors line up exactly. */
function HConnector({ label, twoWay = false }: { label: string; twoWay?: boolean }) {
  return (
    <div className="flex flex-row items-center justify-center gap-2 sm:h-full sm:w-24 sm:flex-col sm:gap-0">
      <span className="order-2 text-center text-xs font-medium leading-tight text-slate-500 dark:text-slate-400 sm:order-1 sm:flex sm:h-9 sm:items-end sm:justify-center">
        {label}
      </span>
      <span
        aria-hidden
        className="order-1 flex flex-col items-center text-slate-400 dark:text-slate-500 sm:order-2 sm:my-1.5 sm:w-full sm:flex-row"
      >
        {twoWay && (
          <ArrowRight className="w-3.5 h-3.5 shrink-0 -rotate-90 -mb-1 sm:mb-0 sm:-mr-1 sm:rotate-180" />
        )}
        <span className="h-5 w-px bg-current sm:h-px sm:w-auto sm:flex-1" />
        <ArrowRight className="w-3.5 h-3.5 shrink-0 rotate-90 -mt-1 sm:mt-0 sm:-ml-1 sm:rotate-0" />
      </span>
      <span aria-hidden className="hidden sm:order-3 sm:block sm:h-9" />
    </div>
  );
}

/** Vertical (below the Docs hub). One head at each end for the two-way MCP
 *  link. The arrow is centred on the diagram's vertical axis (Docs → Connect
 *  must stay one straight line), so the label hangs off its side — absolutely
 *  positioned, never part of the centred group — where it reads as naming the
 *  edge rather than captioning the box below it. The label sits to the LEFT
 *  because the elbow leaves the same card edge just to the right; put it back
 *  on the right and the two collide. */
function VConnector({ label, twoWay = false }: { label: string; twoWay?: boolean }) {
  return (
    <div className="relative flex justify-center py-1">
      <span aria-hidden className="flex flex-col items-center text-slate-400 dark:text-slate-500">
        <ArrowRight className="w-3.5 h-3.5 -rotate-90 -mb-1" />
        <span className="h-5 w-px bg-current" />
        {twoWay && <ArrowRight className="w-3.5 h-3.5 rotate-90 -mt-1" />}
      </span>
      <span className="absolute right-1/2 top-1/2 mr-4 -translate-y-1/2 whitespace-nowrap text-xs font-medium leading-tight text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </div>
  );
}

/**
 * The Refine loop, drawn as an elbow because its endpoints are diagonal: Docs
 * sits mid-top-row, Insights bottom-right. It leaves Docs' bottom edge, runs
 * across the connector band, and turns down into Insights — so the line still
 * joins the two cards that actually refine each other, with a head at each end
 * because the loop runs both ways.
 *
 * Whichever of Docs' four edges goes diagonal is the one that gets drawn: the
 * grid gives a box three neighbours, and Docs needs four (Capture in, Published
 * out, MCP down, Refine). Swap two cards and this component follows the
 * diagonal — it is not specific to Refine.
 *
 * Geometry is arithmetic on the grid rather than magic numbers: the element
 * spans columns 3–5 of the band, whose outer columns are 1fr each either side
 * of the fixed-width connector column, so each card's centre is exactly half a
 * 1fr column in from its side. GRID_INSET is that half-column; keep it in step
 * with the grid's `sm:gap-x-2` (2 gaps) and HConnector's `sm:w-24`.
 * ELBOW_START offsets the drop clear of the MCP connector — and its label —
 * leaving Docs from the same edge. sm+ only, like the MCP link it runs beside.
 */
const GRID_INSET = 'calc((100% - 7rem) / 4)'; // 6rem connector column + 2 × 0.5rem gap
const ELBOW_START = 'calc((100% - 7rem) / 4 + 2rem)';

function ElbowConnector({ label, twoWay = false }: { label: string; twoWay?: boolean }) {
  return (
    <div
      aria-hidden
      className="relative h-full min-h-[3.5rem] text-slate-400 dark:text-slate-500"
    >
      {/* out of Docs' bottom edge, then across to the far card's column */}
      <span
        className="absolute bottom-1/2 top-0 flex -translate-x-1/2 flex-col items-center"
        style={{ left: ELBOW_START }}
      >
        {twoWay && <ArrowRight className="w-3.5 h-3.5 shrink-0 -rotate-90 -mb-1" />}
        <span className="w-px flex-1 bg-current" />
      </span>
      <span
        className="absolute top-1/2 h-px bg-current"
        style={{ left: ELBOW_START, right: GRID_INSET }}
      />
      {/* down into the far card */}
      <span
        className="absolute bottom-0 top-1/2 flex -translate-x-1/2 flex-col items-center"
        style={{ left: `calc(100% - ${GRID_INSET})` }}
      >
        <span className="w-px flex-1 bg-current" />
        <ArrowRight className="w-3.5 h-3.5 shrink-0 rotate-90 -mt-1" />
      </span>
      {/* centred on the horizontal run: midway between its two ends */}
      <span
        className="absolute bottom-1/2 mb-1 -translate-x-1/2 whitespace-nowrap text-xs font-medium leading-tight text-slate-500 dark:text-slate-400"
        style={{ left: 'calc(50% + 1rem)' }}
      >
        {label}
      </span>
    </div>
  );
}

export default function HubPage() {
  const { user } = useAuthContext();
  const { tenant } = useTenant();
  const { selected: selectedProject } = useProject();

  // Slug resolution mirrors the shared header: token tenant, then hostname,
  // then the cross-subdomain cookie.
  const slug = tenant?.slug ?? getTenantSlug() ?? rememberedTenantSlug();

  const surfaceByKey = Object.fromEntries(SURFACES.map((s) => [s.key, s])) as Record<
    SurfaceKey,
    Surface
  >;

  const noSlugYet = 'Available once your workspace has a URL slug.';
  // With a project selected (header selector), the library opens path-addressed
  // (/p/{projectSlug}/docs) so the choice follows the user across the host.
  const docsPath = selectedProject
    ? `${PROJECT_PATH_PREFIX}/${selectedProject.slug}/docs`
    : '/docs';

  // Capture stays a surface node — its label and copy come from the shared
  // module, so this one node cannot drift from the switcher. captureAppUrl
  // handles the branded host or the neutral app.systemdox.com fallback.
  const capture: FlowNode = {
    label: surfaceByKey.app.label,
    Icon: IconCapture,
    gradient: 'from-blue-600 to-cyan-500',
    description: surfaceByKey.app.tagline,
    href: captureAppUrl('/', slug),
  };
  // Docs — the doc library you organise and edit, central to everything. Named
  // for the nav pillar and linked to /docs (an exact box↔nav↔URL match); the
  // tenant root would be this hub itself, a self-link.
  const documentNode: FlowNode = slug
    ? {
        label: 'Docs',
        Icon: IconDocs,
        gradient: 'from-teal-500 to-emerald-500',
        description: 'Organise, structure and edit the whole doc library — the hub of everything.',
        href: tenantUrl(slug, docsPath),
      }
    : { label: 'Docs', Icon: IconDocs, gradient: '', description: noSlugYet, disabled: true };
  // Insights — gap analysis of the library, feeding refinements back into
  // Docs. "Gap analysis" leads the description (the scan-token enterprise
  // evaluators look for).
  const insights: FlowNode = slug
    ? {
        label: 'Insights',
        Icon: IconGap,
        gradient: 'from-emerald-500 to-green-500',
        description:
          'Gap analysis for the whole library — find what is missing and what to refine next.',
        href: tenantUrl(slug, '/insights'),
      }
    : { label: 'Insights', Icon: IconGap, gradient: '', description: noSlugYet, disabled: true };
  // Knowledge Base — the in-app reader over your GitHub-sourced docs, governed
  // by each member's space grants (one surface, ADR-032).
  const shared: FlowNode = slug
    ? {
        label: 'Knowledge Base',
        Icon: IconKnowledgeBase,
        gradient: 'from-amber-500 to-orange-500',
        description:
          'Browse your organisation’s documentation, read live from GitHub and governed by each member’s access.',
        href: tenantUrl(slug, '/docs'),
      }
    : {
        label: 'Knowledge Base',
        Icon: IconKnowledgeBase,
        gradient: '',
        description: noSlugYet,
        disabled: true,
      };
  // Plan — the standalone planning tool on the execution path. It turns a
  // feature description into epics and issues grounded in the docs library,
  // populates a GitHub Project board, and hands off to the agents on Connect.
  // A deliberate tool pillar, not a documentation pillar — its entry lives
  // here, never on the /docs hub. Named for the /plan pillar it opens (an
  // exact box↔nav↔URL match).
  const plan: FlowNode = slug
    ? {
        label: 'Plan',
        Icon: IconPlan,
        gradient: 'from-indigo-500 to-violet-500',
        description:
          'Turn an idea into epics and issues on a GitHub Project board — scoped by your docs and standards, ready for the agents to pick up.',
        href: tenantUrl(slug, '/plan'),
      }
    : { label: 'Plan', Icon: IconPlan, gradient: '', description: noSlugYet, disabled: true };
  // Connect — the AI agents on the MCP link. Named for the /connect pillar the
  // card opens (an exact box↔nav↔URL match); the copy keeps the actors (Claude,
  // Codex) as the counterparty on that edge.
  const agent: FlowNode = slug
    ? {
        label: 'Connect',
        Icon: IconAgents,
        gradient: 'from-violet-500 to-purple-500',
        description:
          'The coding agents you build with — Claude, Codex and the like. They read context and write it back over SystemDox MCP.',
        href: tenantUrl(slug, '/connect'),
      }
    : {
        label: 'Connect',
        Icon: IconAgents,
        gradient: 'from-violet-500 to-purple-500',
        description:
          'The coding agents you build with — Claude, Codex and the like. They read context and write it back over SystemDox MCP.',
      };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto w-full max-w-5xl space-y-8">
        <div className="flex items-center gap-3">
          <img src="/systemdox-logo.png" alt="" className="w-10 h-10 object-contain rounded-xl" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Hi {user?.fullName || user?.email || 'there'}!
          </h1>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            Where to?
          </h2>
          <p className="mt-1 mb-5 text-sm text-slate-500 dark:text-slate-400">
            {surfaceByKey.app.label} feeds {documentNode.label}, the hub of everything —
            published as the knowledge base your whole organisation reads, connected
            over MCP to the AI agents you build with, and refined by what Insights surfaces.
            Plan turns that documented context into board-ready work and hands it to those agents.
          </p>
          {/*
            One diagram, two card rows. The top row is the pipeline itself —
            Capture → Docs → Knowledge Base, i.e. capture it, document it,
            publish it. The bottom row is the tools working off that library:
            Plan hands its board off to Connect, whose agents sit under Docs on
            the two-way SystemDox MCP link, and Insights sits at the end.
            Between the rows, one connector band carries the two edges out of
            Docs that leave its row — MCP straight down, and the two-way Refine
            loop as an elbow out to Insights' column (Docs has four edges and a
            two-row grid only gives a box three neighbours, so the one that goes
            diagonal is drawn rather than laid out).

            DOM order = the mobile stack (Capture → Docs → Knowledge Base, then
            Plan and its hand-off above Connect, and Insights last);
            `sm:col/row-start` re-place it into two rows on wider screens, where
            DOM order no longer matters. The two band edges are `hidden` below
            `sm`: neither one's endpoints stay adjacent in a single column, so
            left in they would caption whatever box followed them — MCP reading
            as Plan handing off *through* MCP, Refine as Connect looping with
            Insights. Published stays visible there because its endpoints really
            are adjacent in the stack, as does Hand off.
          */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:gap-x-2 sm:gap-y-2 sm:items-stretch">
            {/* Top row, the pipeline: capture it, document it, publish it. */}
            <div className="sm:col-start-1 sm:row-start-1">
              <FlowCard node={capture} />
            </div>
            <div className="sm:col-start-2 sm:row-start-1">
              <HConnector label={surfaceByKey.app.handoff ?? ''} />
            </div>
            <div className="sm:col-start-3 sm:row-start-1">
              <FlowCard node={documentNode} highlight />
            </div>
            <div className="sm:col-start-4 sm:row-start-1">
              <HConnector label="Published" />
            </div>
            <div className="sm:col-start-5 sm:row-start-1">
              <FlowCard node={shared} />
            </div>

            {/* The connector band: the two edges out of the Docs hub that leave
                its row. Cross-axis only — hidden once the diagram stacks (see
                the note above). */}
            <div className="hidden sm:col-start-3 sm:row-start-2 sm:block">
              <VConnector label="SystemDox MCP" twoWay />
            </div>
            <div className="hidden sm:col-start-3 sm:col-end-6 sm:row-start-2 sm:block">
              <ElbowConnector label="Refine" twoWay />
            </div>

            {/* Bottom row, the tools working off that library: Plan populates a
                board and hands it off to Connect, whose agents execute over the
                MCP link above; Insights sits at the end on the Refine loop. */}
            <div className="sm:col-start-1 sm:row-start-3">
              <FlowCard node={plan} />
            </div>
            <div className="sm:col-start-2 sm:row-start-3">
              <HConnector label="Hand off" />
            </div>
            <div className="sm:col-start-3 sm:row-start-3">
              <FlowCard node={agent} />
            </div>
            <div className="sm:col-start-5 sm:row-start-3">
              <FlowCard node={insights} />
            </div>
          </div>
        </div>

        {/* The workspace dashboard — the console's former hub. Cross-host link
            targets (and the /p/{projectSlug} console prefix) are the child's
            concern; it only needs the resolved slugs. */}
        <LaunchDashboard slug={slug} projectSlug={selectedProject?.slug ?? null} />
      </div>
    </div>
  );
}
